import React from "react";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { campaignHistory, duplicateCampaign, campaignIntelligence } from "@/lib/api";
import { CampaignIntelDialog } from "@/components/MarketingScore";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, CheckCircle, FolderSimple, ChartBar, Clock } from "@phosphor-icons/react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function CampaignHistoryPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const { data: camps = [], isLoading } = useQuery({
    queryKey: ["campaign-history", activeId],
    queryFn: () => campaignHistory(activeId),
    enabled: !!activeId,
  });
  const dup = useMutation({
    mutationFn: (id) => duplicateCampaign(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["campaign-history", activeId] });
      toast.success(r.mode === "monthly"
        ? `Duplicating for ${r.month} — a fresh month is generating now (check the Approval Queue soon)`
        : `Campaign duplicated — ${r.items} item(s) cloned as drafts`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Duplicate failed"),
  });
  const [intelView, setIntelView] = useState(null); // {name, report}
  const [intelBusyId, setIntelBusyId] = useState(null);
  const runIntel = async (c) => {
    setIntelBusyId(c.id);
    toast.loading("AI CMO is analyzing the campaign…", { id: "cintel" });
    try {
      const report = await campaignIntelligence(c.id);
      toast.dismiss("cintel");
      setIntelView({ name: c.name, report });
    } catch (e) { toast.dismiss("cintel"); toast.error(e?.response?.data?.detail || "Analysis failed — try again"); }
    setIntelBusyId(null);
  };

  return (
    <div data-testid="campaign-history-page" className="space-y-6">
      <div>
        <h1 className="font-head text-2xl text-white flex items-center gap-2"><ClockIcon /> Campaign History</h1>
        <p className="text-sm text-[#8a8a8a] mt-1">Every campaign your AI team has run — with results, estimates and one-click duplication.</p>
      </div>
      {isLoading && <div className="text-sm text-[#7a7a7a]">Loading campaigns…</div>}
      {!isLoading && camps.length === 0 && (
        <div data-testid="history-empty" className="card-matte rounded-xl p-8 text-center text-sm text-[#7a7a7a]">
          No campaigns yet — generate your first month from the AI Monthly Planner.
        </div>
      )}
      <div className="space-y-3">
        {camps.map((c) => (
          <div key={c.id} data-testid={`history-row-${c.id}`} className="card-matte rounded-xl p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="min-w-[220px]">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <FolderSimple size={15} className="brand-text" weight="fill" /> {c.name}
                {c.complete && <span data-testid={`history-complete-${c.id}`} className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400"><CheckCircle size={11} weight="fill" /> COMPLETE</span>}
              </div>
              <div className="text-[11px] text-[#7a7a7a] mt-0.5 capitalize">{c.objective || c.source.replace(/_/g, " ")} · Created {fmtDate(c.created_at)}</div>
            </div>
            <div className="flex items-center gap-5 text-xs">
              <span className="text-[#9a9a9a]">Generated <b className="text-white">{c.total}</b></span>
              <span className="text-[#9a9a9a]">Published <b className="text-emerald-400">{c.published}</b></span>
              <span className="text-[#9a9a9a]">Failed <b className={c.failed ? "text-red-400" : "text-white"}>{c.failed}</b></span>
              <span className="flex items-center gap-1.5 text-[#9a9a9a]"><ChartBar size={13} className="text-sky-400" />
                Est. reach <b className="text-white">{(c.estimates?.reach || 0).toLocaleString()}</b> · clicks <b className="text-white">{(c.estimates?.clicks || 0).toLocaleString()}</b>
                <span className="text-[9px]">(estimates)</span></span>
            </div>
            <Button data-testid={`history-intel-${c.id}`} size="sm" disabled={intelBusyId === c.id} onClick={() => runIntel(c)}
              className="ml-auto h-8 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 border border-sky-500/25">
              <ChartBar size={14} className="mr-1.5" /> {intelBusyId === c.id ? "Analyzing…" : "Intelligence"}
            </Button>
            <Button data-testid={`history-duplicate-${c.id}`} size="sm" disabled={dup.isPending} onClick={() => dup.mutate(c.id)}
              className="h-8 bg-[#6B3DFF]/15 hover:bg-[#6B3DFF]/25 text-[#C39BFF] border border-[#6B3DFF]/25">
              <Copy size={14} className="mr-1.5" /> {dup.isPending ? "Duplicating…" : "Duplicate"}
            </Button>
          </div>
        ))}
      </div>
      <CampaignIntelDialog open={!!intelView} onClose={() => setIntelView(null)}
        name={intelView?.name} report={intelView?.report} />
    </div>
  );
}

const ClockIcon = () => <Clock className="brand-text" weight="fill" />;
